import React, { useEffect, useMemo, useState } from "react";
import ReportCover from "@/components/marketing/ReportCover";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown, TriangleAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";

const FALLBACK_SECTORS = [
  { slug: "mortgages", name: "Mortgages", base_cpl: 38 },
  { slug: "business-finance", name: "Business Finance", base_cpl: 52 },
  { slug: "debt-help", name: "Debt Help", base_cpl: 28 },
  { slug: "payday-loans", name: "Payday & Short-Term", base_cpl: 14 },
  { slug: "claims-management", name: "Claims Management", base_cpl: 42 },
  { slug: "life-insurance", name: "Life Insurance", base_cpl: 34 },
  { slug: "investor-leads", name: "Investor Leads", base_cpl: 120 },
  { slug: "wealth-management", name: "Wealth Management", base_cpl: 95 },
  { slug: "commercial-finance", name: "Commercial Finance", base_cpl: 145 },
  { slug: "property-investment", name: "Property Investment", base_cpl: 88 },
];

const gbp = (n, dp = 0) => `£${Number(n).toLocaleString("en-GB", { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;

export default function RoiCalculator({ ctaHref = "/buy-leads#calculator" }) {
  const [sectors, setSectors] = useState(FALLBACK_SECTORS);
  const [slug, setSlug] = useState("mortgages");
  const [budget, setBudget] = useState(15000);
  const [closeRate, setCloseRate] = useState(10);
  const [value, setValue] = useState(1500);

  useEffect(() => {
    api.get("/lead-packs/meta")
      .then((r) => {
        if (Array.isArray(r.data?.sectors) && r.data.sectors.length) setSectors(r.data.sectors);
      })
      .catch(() => {});
  }, []);

  const out = useMemo(() => {
    const cpl = sectors.find((s) => s.slug === slug)?.base_cpl || 40;
    const leads = Math.floor(budget / cpl);
    const customers = leads * (closeRate / 100);
    const revenue = customers * value;
    const multiple = budget > 0 ? revenue / budget : 0;
    const cpa = customers > 0 ? budget / customers : 0;
    const breakeven = value > 0 ? (cpl / value) * 100 : 0;
    return { cpl, leads, customers, revenue, multiple, cpa, breakeven };
  }, [sectors, slug, budget, closeRate, value]);

  return (
    <section className="py-20 sm:py-28 bg-white border-b border-slate-200/70" data-testid="roi-calculator">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#0055FF]">Model your return</p>
          <h2 className="mt-3 text-4xl sm:text-5xl tracking-[-0.03em] font-medium text-slate-900">What would your budget actually return?</h2>
          <p className="mt-4 text-slate-600 text-lg">Pick your sector, set a monthly budget and your close rate — see the economics before you spend a pound.</p>
        </div>

        <div className="mt-12 rounded-3xl border border-slate-200 overflow-hidden shadow-[0_24px_60px_-30px_rgba(15,23,42,0.25)]">
          <div className="grid lg:grid-cols-12">
            {/* Inputs */}
            <div className="lg:col-span-7 bg-white p-7 sm:p-10 space-y-8">
              <div>
                <FieldLabel>Sector</FieldLabel>
                <div className="relative mt-2">
                  <select
                    data-testid="roi-sector-select"
                    value={slug}
                    onChange={(e) => setSlug(e.target.value)}
                    className="w-full h-11 appearance-none rounded-lg border border-slate-300 bg-white pl-3 pr-10 text-sm font-medium text-slate-900 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#0055FF]/30"
                  >
                    {sectors.map((s) => (
                      <option key={s.slug} value={s.slug}>{`${s.name} — ${gbp(s.base_cpl)} / lead`}</option>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 size-4 text-slate-500" />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <FieldLabel>Monthly budget</FieldLabel>
                  <span className="text-sm font-semibold text-slate-900 tabular-nums" data-testid="roi-budget-display">{gbp(budget)}</span>
                </div>
                <input
                  type="range" min={5000} max={50000} step={500}
                  value={budget}
                  onChange={(e) => setBudget(Number(e.target.value))}
                  data-testid="roi-budget-range"
                  className="mt-3 w-full accent-[#0055FF]"
                />
                <div className="flex justify-between text-[11px] font-mono text-slate-400 mt-1">
                  <span>£5,000</span><span>£50,000</span>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center justify-between">
                    <FieldLabel>Your close rate</FieldLabel>
                    <span className="text-sm font-semibold text-slate-900 tabular-nums" data-testid="roi-close-display">{closeRate}%</span>
                  </div>
                  <input
                    type="range" min={1} max={40} step={1}
                    value={closeRate}
                    onChange={(e) => setCloseRate(Number(e.target.value))}
                    data-testid="roi-close-rate-range"
                    className="mt-3 w-full accent-[#0055FF]"
                  />
                  <div className="flex justify-between text-[11px] font-mono text-slate-400 mt-1">
                    <span>1%</span><span>40%</span>
                  </div>
                </div>
                <div>
                  <FieldLabel>Avg. customer value (£)</FieldLabel>
                  <Input
                    type="number" min={50}
                    value={value}
                    onChange={(e) => setValue(Math.max(0, Number(e.target.value)))}
                    data-testid="roi-value-input"
                    className="mt-2 h-11 font-medium tabular-nums"
                  />
                  <p className="mt-1.5 text-[11px] text-slate-400">Revenue or commission per closed customer.</p>
                </div>
              </div>
            </div>

            {/* Results */}
            <div className="lg:col-span-5 bg-slate-950 text-white p-7 sm:p-10 flex flex-col">
              <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-white/50">Projected monthly outcome</div>
              <div className="mt-6 grid grid-cols-2 gap-x-6 gap-y-7 flex-1">
                <ResultStat testid="roi-leads" label="Exclusive leads" value={out.leads.toLocaleString("en-GB")} sub={`at ${gbp(out.cpl)} / lead — fixed`} />
                <ResultStat testid="roi-customers" label="Est. new customers" value={out.customers.toFixed(1)} sub={`at your ${closeRate}% close rate`} />
                <ResultStat testid="roi-revenue" label="Projected revenue" value={gbp(out.revenue)} sub={`${out.customers.toFixed(1)} × ${gbp(value)} value`} accent />
                <ResultStat testid="roi-multiple" label="Return multiple" value={`${out.multiple.toFixed(1)}×`} sub="revenue ÷ lead spend" accent={out.multiple >= 1} warn={out.multiple < 1} />
                <ResultStat testid="roi-cpa" label="Cost per acquisition" value={out.customers > 0 ? gbp(out.cpa) : "—"} sub="lead cost ÷ close rate — not a fee" />
                <ResultStat testid="roi-breakeven" label="Break-even close rate" value={out.breakeven > 100 ? ">100%" : `${out.breakeven.toFixed(1)}%`} sub="close rate that covers spend" warn={out.breakeven > 100} />
              </div>
              {out.multiple < 1 && (
                <div className="mt-6 flex items-start gap-2.5 rounded-lg border border-amber-400/30 bg-amber-400/10 px-3.5 py-3 text-[12px] leading-relaxed text-amber-200" data-testid="roi-loss-warning">
                  <TriangleAlert className="size-4 shrink-0 mt-0.5" />
                  <span>These inputs don't break even — check your average customer value. Most {sectors.find((s) => s.slug === slug)?.name || "finance"} buyers earn far more than {gbp(out.cpl)} per closed customer.</span>
                </div>
              )}
              <div className="mt-8 pt-6 border-t border-white/10">
                <a href={ctaHref}>
                  <Button data-testid="roi-cta" className="w-full rounded-full h-11 bg-white text-slate-900 hover:bg-slate-100 font-semibold">
                    Build this order <ArrowRight className="size-4" />
                  </Button>
                </a>
                <p className="mt-3 text-[11px] text-white/40 leading-relaxed text-center">
                  Modelled on the {gbp(out.cpl)} base CPL for this sector. Exact pricing is shown in the order builder.
                </p>
                <Link to="/benchmark-report" data-testid="roi-benchmark-link"
                      className="mt-3 flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.04] p-3 transition-colors hover:border-white/25 hover:bg-white/[0.07]">
                  <ReportCover scale={0.42} tilt={-3} testId="roi-benchmark-cover" />
                  <span className="min-w-0">
                    <span className="block text-[12.5px] font-semibold text-white">Want all ten sectors?</span>
                    <span className="mt-0.5 block text-[11.5px] text-white/50">The 2026 CPL Benchmark Report — free PDF</span>
                  </span>
                  <ArrowRight className="ml-auto size-4 shrink-0 text-[#60A5FA]" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FieldLabel({ children }) {
  return <span className="text-[11px] uppercase tracking-[0.16em] font-semibold text-slate-500">{children}</span>;
}

function ResultStat({ label, value, sub, accent, warn, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10.5px] uppercase tracking-[0.14em] text-white/40 font-semibold">{label}</div>
      <div className={`mt-1.5 text-2xl sm:text-[28px] font-semibold tracking-tight tabular-nums ${warn ? "text-amber-400" : accent ? "text-[#60A5FA]" : "text-white"}`}>
        {value}
      </div>
      {sub && <div className="mt-1 text-[11px] text-white/35 leading-snug">{sub}</div>}
    </div>
  );
}
